"use strict";
/**
 * Dashboard — SPEC §5.1 (the owner's landing screen), §5.7 (the money lines it
 * shares with the Overall Report), §14 (reference figures from a real day).
 *
 * This module is the **contract plus the arithmetic**. It is pure: no Prisma, no
 * Nest, no React, no clock of its own. The API projects rows out of Postgres and
 * hands them to these functions; the staff app renders the DTOs they produce.
 * Putting the maths here means the numbers the owner sees are unit-tested rather
 * than buried in a query handler or a chart component.
 *
 * Three rules are load-bearing, and every function here is built around them:
 *
 * 1. **The business day, never `createdAt`.** Venues trade past midnight, so a
 *    02:00 sale belongs to the previous business date (§1, §4.1). Rows carry a
 *    denormalised `businessDate`; where an instant has to be bucketed live,
 *    {@link bucketInstantsByBusinessDate} routes it through `resolveBusinessDate`
 *    rather than reading a calendar date off the timestamp. Getting this wrong
 *    silently misstates every figure on the screen.
 *
 * 2. **Money is integer millidinars.** Internally {@link Millidinars} bigints;
 *    across the API boundary, decimal strings ({@link MdString}) because JSON has
 *    no bigint. No float ever touches a monetary value — the only numbers that
 *    become floats are percentages and hour counts, which are display-only.
 *
 * 3. **Empty is normal, and must not read as broken.** A brand-new venue has no
 *    sales; mid-morning there is one hour of data. Every ratio here goes through
 *    {@link safeRatio}, so a venue with no devices, no previous period or no
 *    revenue yields `0` or an explicit `null` — never `NaN`, never `Infinity`,
 *    never `-0` — and the chart series are gap-filled so a sparse day still draws
 *    a continuous line.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DASHBOARD_PERIODS = void 0;
exports.isDashboardPeriod = isDashboardPeriod;
exports.safeRatio = safeRatio;
exports.roundTo = roundTo;
exports.percentOf = percentOf;
exports.percentOfMd = percentOfMd;
exports.formatMd = formatMd;
exports.startOfBusinessMonth = startOfBusinessMonth;
exports.endOfBusinessMonth = endOfBusinessMonth;
exports.periodRange = periodRange;
exports.previousPeriodRange = previousPeriodRange;
exports.eachBusinessDate = eachBusinessDate;
exports.businessDateCount = businessDateCount;
exports.groupByBusinessDate = groupByBusinessDate;
exports.bucketInstantsByBusinessDate = bucketInstantsByBusinessDate;
exports.sumInRange = sumInRange;
exports.compareToPrevious = compareToPrevious;
exports.computeTargetProgress = computeTargetProgress;
exports.summariseDeviceUsage = summariseDeviceUsage;
exports.computeOccupancy = computeOccupancy;
exports.trailingMonths = trailingMonths;
exports.buildMonthlySalesSeries = buildMonthlySalesSeries;
exports.topEarningDevices = topEarningDevices;
exports.buildSalesSeries = buildSalesSeries;
exports.topCustomers = topCustomers;
exports.lowStockAlerts = lowStockAlerts;
exports.summarisePeriod = summarisePeriod;
exports.estimateSales = estimateSales;
exports.tickEstimatedSales = tickEstimatedSales;
exports.emptyDashboardPayload = emptyDashboardPayload;
exports.rangeToInstantBounds = rangeToInstantBounds;
const business_day_1 = require("./business-day");
const money_1 = require("./money");
exports.DASHBOARD_PERIODS = ['DAY', 'MONTH'];
function isDashboardPeriod(value) {
    return value === 'DAY' || value === 'MONTH';
}
/* -------------------------------------------------------------------------- */
/* Safe arithmetic — the anti-NaN layer                                       */
/* -------------------------------------------------------------------------- */
/**
 * `numerator / denominator`, but total: a zero, negative, or non-finite
 * denominator yields `fallback` (default `0`) instead of `Infinity` or `NaN`.
 *
 * Every division in this module goes through here. That is the whole defence
 * against a fresh venue rendering "NaN%" on the screen that sells the product.
 */
function safeRatio(numerator, denominator, fallback = 0) {
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
        return fallback;
    }
    const ratio = numerator / denominator;
    return Number.isFinite(ratio) ? ratio : fallback;
}
/** Round to `decimals` places, normalising `-0` to `0` so it never renders as "-0". */
function roundTo(value, decimals = 2) {
    if (!Number.isFinite(value))
        return 0;
    const factor = 10 ** decimals;
    const rounded = Math.round(value * factor) / factor;
    return rounded === 0 ? 0 : rounded;
}
/** `part / whole` as a percentage clamped to `0..100`, rounded for display. */
function percentOf(part, whole, decimals = 1) {
    const pct = safeRatio(part, whole) * 100;
    return roundTo(Math.min(Math.max(pct, 0), 100), decimals);
}
/** Millidinars ratio without leaving bigint precision until the final divide. */
function ratioOfMd(part, whole) {
    if (whole <= 0n)
        return 0;
    // Scale by 1e6 in bigint space, then one float division — keeps precision for
    // amounts far beyond Number.MAX_SAFE_INTEGER millidinars.
    const scaled = (part * 1000000n) / whole;
    return Number(scaled) / 1_000_000;
}
/** Percentage of a millidinar total, clamped to `0..100`. */
function percentOfMd(part, whole, decimals = 1) {
    const pct = ratioOfMd(part, whole) * 100;
    return roundTo(Math.min(Math.max(pct, 0), 100), decimals);
}
/** Render a transported {@link MdString} for display. Never used in billing math. */
function formatMd(amount, options = {}) {
    return (0, money_1.formatIqd)((0, money_1.fromMillidinars)(amount), options);
}
/* -------------------------------------------------------------------------- */
/* Period resolution                                                          */
/* -------------------------------------------------------------------------- */
/** First business date of the calendar month containing `date`. */
function startOfBusinessMonth(date) {
    return `${date.slice(0, 7)}-01`;
}
/** Last business date of the calendar month containing `date`. */
function endOfBusinessMonth(date) {
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    // Day 0 of the next month is the last day of this one.
    const last = new Date(Date.UTC(year, month, 0));
    return `${String(last.getUTCFullYear()).padStart(4, '0')}-${String(last.getUTCMonth() + 1).padStart(2, '0')}-${String(last.getUTCDate()).padStart(2, '0')}`;
}
/**
 * The business-date range a dashboard period covers, anchored on the business
 * date currently in progress.
 *
 * `DAY` is that single business date. `MONTH` runs from the 1st to the anchor —
 * month-to-date, not the whole calendar month, because comparing a half-finished
 * month against a complete one is how dashboards lie.
 */
function periodRange(anchor, period) {
    return period === 'DAY'
        ? { from: anchor, to: anchor }
        : { from: startOfBusinessMonth(anchor), to: anchor };
}
/**
 * The equivalent range one period earlier, for the vs-previous comparison (§5.1).
 *
 * `DAY` is simply yesterday. `MONTH` is the *same span of days* in the previous
 * month — the 1st through the same day-of-month — so a month-to-date figure on
 * the 9th is compared against the previous month's first nine days, not its full
 * total. Where the previous month is shorter (31 Mar -> Feb) the range is clamped
 * to that month's last day.
 */
function previousPeriodRange(anchor, period) {
    if (period === 'DAY') {
        const yesterday = (0, business_day_1.addDaysToBusinessDate)(anchor, -1);
        return { from: yesterday, to: yesterday };
    }
    const firstOfThisMonth = startOfBusinessMonth(anchor);
    const lastOfPrevMonth = (0, business_day_1.addDaysToBusinessDate)(firstOfThisMonth, -1);
    const firstOfPrevMonth = startOfBusinessMonth(lastOfPrevMonth);
    const dayOfMonth = Number(anchor.slice(8, 10));
    const prevMonthLastDay = Number(lastOfPrevMonth.slice(8, 10));
    const clampedDay = Math.min(dayOfMonth, prevMonthLastDay);
    return {
        from: firstOfPrevMonth,
        to: `${firstOfPrevMonth.slice(0, 7)}-${String(clampedDay).padStart(2, '0')}`,
    };
}
/** Every business date in an inclusive range, ascending. Empty if `to < from`. */
function eachBusinessDate(range) {
    const dates = [];
    let cursor = range.from;
    // Guard against a malformed range spinning forever.
    for (let i = 0; i < 4000 && cursor <= range.to; i += 1) {
        dates.push(cursor);
        cursor = (0, business_day_1.addDaysToBusinessDate)(cursor, 1);
    }
    return dates;
}
/** Number of business dates in an inclusive range. */
function businessDateCount(range) {
    return eachBusinessDate(range).length;
}
/**
 * Group rows that already carry `businessDate`. Cheap, and the normal path —
 * the column was written at insert time by the same rule.
 */
function groupByBusinessDate(rows) {
    const buckets = new Map();
    for (const row of rows) {
        const bucket = buckets.get(row.businessDate);
        if (bucket)
            bucket.push(row);
        else
            buckets.set(row.businessDate, [row]);
    }
    return buckets;
}
/**
 * Group rows that carry only an instant, by resolving each through the venue's
 * Opening Time.
 *
 * This is the path for live/streamed data that has not been through a write with
 * a denormalised column — and it is where the after-midnight rule actually bites:
 * with a 09:00 boundary, `2026-07-26T02:00+03:00` buckets to `2026-07-25`, not
 * `2026-07-26`. Never substitute `instant.toISOString().slice(0, 10)` here.
 */
function bucketInstantsByBusinessDate(rows, instantOf, config) {
    const buckets = new Map();
    for (const row of rows) {
        const date = (0, business_day_1.resolveBusinessDate)(instantOf(row), config);
        const bucket = buckets.get(date);
        if (bucket)
            bucket.push(row);
        else
            buckets.set(date, [row]);
    }
    return buckets;
}
/** Sum a money field over rows falling inside an inclusive business-date range. */
function sumInRange(rows, range, amountOf) {
    return (0, money_1.sum)(rows
        .filter((row) => row.businessDate >= range.from && row.businessDate <= range.to)
        .map(amountOf));
}
function compareToPrevious(current, previous) {
    const change = (0, money_1.subtract)(current, previous);
    const direction = change > 0n ? 'up' : change < 0n ? 'down' : 'flat';
    return {
        currentMd: (0, money_1.serializeMd)(current),
        previousMd: (0, money_1.serializeMd)(previous),
        changeMd: (0, money_1.serializeMd)(change),
        // Undefined against a zero baseline — deliberately null, not Infinity.
        changePercent: previous === 0n ? null : roundTo(ratioOfMd(change, previous) * 100, 1),
        direction,
    };
}
function computeTargetProgress(achieved, target) {
    const hasTarget = target > 0n;
    const remaining = target > achieved ? (0, money_1.subtract)(target, achieved) : money_1.ZERO_MD;
    return {
        targetMd: (0, money_1.serializeMd)(target),
        achievedMd: (0, money_1.serializeMd)(achieved),
        remainingMd: (0, money_1.serializeMd)(remaining),
        // No target configured => 0%, not a division by zero.
        percent: hasTarget ? percentOfMd(achieved, target) : 0,
        isMet: hasTarget && achieved >= target,
        hasTarget,
    };
}
/* -------------------------------------------------------------------------- */
/* Device usage by category                                                   */
/* -------------------------------------------------------------------------- */
const SECONDS_PER_HOUR = 3600;
/**
 * Utilisation, hours and revenue per device category (§5.1).
 *
 *     utilisation = billed device-seconds / (devices * open seconds * elapsed days)
 *
 * Every category is emitted, including ones with no devices and no sessions, so
 * the table has stable rows instead of appearing and disappearing between
 * refreshes. A category with zero devices reports `0%` rather than `NaN`, and the
 * result is clamped to `100%` — overlapping or transferred sessions can otherwise
 * bill more device-seconds than the wall clock offered, and ">100% utilised" reads
 * as a bug to an owner.
 */
function summariseDeviceUsage(input) {
    const { categories, devices, sessions, openSecondsPerDay, elapsedDays } = input;
    const deviceCounts = new Map();
    for (const device of devices) {
        deviceCounts.set(device.categoryId, (deviceCounts.get(device.categoryId) ?? 0) + 1);
    }
    const secondsByCategory = new Map();
    const revenueByCategory = new Map();
    for (const session of sessions) {
        const seconds = Number.isFinite(session.billedSeconds) ? Math.max(session.billedSeconds, 0) : 0;
        secondsByCategory.set(session.categoryId, (secondsByCategory.get(session.categoryId) ?? 0) + seconds);
        revenueByCategory.set(session.categoryId, ((revenueByCategory.get(session.categoryId) ?? money_1.ZERO_MD) + session.totalMd));
    }
    const totalRevenue = (0, money_1.sum)(revenueByCategory.values());
    const safeOpenSeconds = Math.max(openSecondsPerDay, 0);
    const safeElapsedDays = Math.max(elapsedDays, 0);
    return categories.map((category) => {
        const deviceCount = deviceCounts.get(category.id) ?? 0;
        const billedSeconds = secondsByCategory.get(category.id) ?? 0;
        const revenue = revenueByCategory.get(category.id) ?? money_1.ZERO_MD;
        const capacitySeconds = Math.round(deviceCount * safeOpenSeconds * safeElapsedDays);
        return {
            categoryId: category.id,
            categoryName: category.name,
            deviceCount,
            billedSeconds,
            billedHours: roundTo(safeRatio(billedSeconds, SECONDS_PER_HOUR), 2),
            capacitySeconds,
            // safeRatio absorbs the no-devices and not-yet-open cases.
            utilisationPercent: percentOf(billedSeconds, capacitySeconds),
            revenueMd: (0, money_1.serializeMd)(revenue),
            revenueSharePercent: percentOfMd(revenue, totalRevenue),
        };
    });
}
/**
 * Occupancy from live device statuses.
 *
 * `ABOUT_TO_END` counts as busy — the device is still occupied and still earning;
 * it is a warning state on the tile, not a free slot. Out-of-service devices are
 * removed from the denominator, so taking a broken PC offline raises occupancy
 * rather than diluting it.
 */
function computeOccupancy(statuses) {
    let busy = 0;
    let available = 0;
    let aboutToEnd = 0;
    let outOfService = 0;
    for (const status of statuses) {
        switch (status) {
            case 'BUSY':
                busy += 1;
                break;
            case 'ABOUT_TO_END':
                aboutToEnd += 1;
                break;
            case 'AVAILABLE':
                available += 1;
                break;
            case 'OUT_OF_SERVICE':
                outOfService += 1;
                break;
            default:
                break;
        }
    }
    const occupied = busy + aboutToEnd;
    const sellable = occupied + available;
    return {
        busy,
        available,
        aboutToEnd,
        outOfService,
        sellable,
        total: statuses.length,
        // No devices => 0%, never NaN.
        percent: percentOf(occupied, sellable),
    };
}
/** Millidinars to float IQD for plotting. Display-only, per `toIqd`'s contract. */
function toIqdNumber(amount) {
    return Number(amount) / 1000;
}
function pointOf(key, device, cafe) {
    return {
        key,
        deviceSalesMd: (0, money_1.serializeMd)(device),
        cafeSalesMd: (0, money_1.serializeMd)(cafe),
        totalMd: (0, money_1.serializeMd)((device + cafe)),
        deviceSalesIqd: toIqdNumber(device),
        cafeSalesIqd: toIqdNumber(cafe),
    };
}
/** The `months` calendar months ending at (and including) `anchorMonth`. */
function trailingMonths(anchorMonth, months = 12) {
    const year = Number(anchorMonth.slice(0, 4));
    const month = Number(anchorMonth.slice(5, 7));
    return Array.from({ length: months }, (_, i) => {
        const d = new Date(Date.UTC(year, month - 1 - (months - 1 - i), 1));
        return `${String(d.getUTCFullYear()).padStart(4, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    });
}
/**
 * The trailing-year sales trend, one bar per calendar month.
 *
 * Gap-filled for the same reason the daily chart is: a month the venue took
 * nothing draws a zero bar, not a hole. Uses the same Sales definition as the
 * KPI (reservation + cafe subtotals) so the bar for this month always agrees
 * with the "Monthly sales" tile beside it — two figures for the same thing is
 * how dashboards lose the owner's trust.
 */
function buildMonthlySalesSeries(rows, anchorMonth, months = 12) {
    const totals = new Map();
    for (const row of rows) {
        const key = row.businessDate.slice(0, 7);
        totals.set(key, ((totals.get(key) ?? money_1.ZERO_MD) + row.salesMd));
    }
    return trailingMonths(anchorMonth, months).map((month) => {
        const salesMd = totals.get(month) ?? money_1.ZERO_MD;
        return { month, salesMd: (0, money_1.serializeMd)(salesMd), salesIqd: toIqdNumber(salesMd) };
    });
}
/**
 * Which devices made the most money in the period, best first.
 *
 * Derived from the SAME session rows utilisation reads — a running session is
 * valued mid-flight, a free-mode session still bills — so this list and the
 * usage table can never disagree about the same machine. Devices that earned
 * nothing are omitted rather than padding the list with zeros; a device that
 * has since been renamed or retired still shows under its id, because the
 * money it took is a fact about the period, not about today's floor.
 */
function topEarningDevices(sessions, devices, limit = 8) {
    const revenue = new Map();
    const counts = new Map();
    for (const session of sessions) {
        revenue.set(session.deviceId, ((revenue.get(session.deviceId) ?? money_1.ZERO_MD) + session.totalMd));
        counts.set(session.deviceId, (counts.get(session.deviceId) ?? 0) + 1);
    }
    const byId = new Map(devices.map((device) => [device.id, device]));
    return [...revenue.entries()]
        .filter(([, earned]) => earned > money_1.ZERO_MD)
        .map(([deviceId, earned]) => ({
        deviceId,
        name: byId.get(deviceId)?.name ?? deviceId,
        category: byId.get(deviceId)?.category ?? '',
        earned,
        sessions: counts.get(deviceId) ?? 0,
    }))
        .sort((a, b) => a.earned < b.earned ? 1 : a.earned > b.earned ? -1 : a.name.localeCompare(b.name))
        .slice(0, limit)
        .map(({ earned, ...row }) => ({ ...row, revenueMd: (0, money_1.serializeMd)(earned) }));
}
/**
 * The device-sales chart series, per day or per month (§5.1).
 *
 * **Gap-filled.** Every bucket in the range is emitted, zero-valued where there
 * were no sales. A venue closed on Monday draws a line through zero instead of a
 * chart that silently skips a day and misleads about the shape of the week — and
 * a brand-new venue gets a flat baseline rather than an empty plot area that
 * looks like a failed fetch.
 */
function buildSalesSeries(rows, range, period) {
    const deviceByKey = new Map();
    const cafeByKey = new Map();
    const keyOf = (date) => (period === 'DAY' ? date : date.slice(0, 7));
    for (const row of rows) {
        if (row.businessDate < range.from || row.businessDate > range.to)
            continue;
        const key = keyOf(row.businessDate);
        deviceByKey.set(key, ((deviceByKey.get(key) ?? money_1.ZERO_MD) + row.deviceSalesMd));
        cafeByKey.set(key, ((cafeByKey.get(key) ?? money_1.ZERO_MD) + row.cafeSalesMd));
    }
    // Enumerate buckets from the range, not from the data, so gaps are filled.
    const keys = [];
    const seen = new Set();
    for (const date of eachBusinessDate(range)) {
        const key = keyOf(date);
        if (!seen.has(key)) {
            seen.add(key);
            keys.push(key);
        }
    }
    return keys.map((key) => pointOf(key, deviceByKey.get(key) ?? money_1.ZERO_MD, cafeByKey.get(key) ?? money_1.ZERO_MD));
}
/**
 * Top customers by spend over the period (§5.1).
 *
 * Ties break on name so the list is stable between refreshes rather than
 * reshuffling equal-spend customers on every poll. Walk-in visitors have no
 * account and are excluded upstream — this ranks registered accounts (§5.5).
 */
function topCustomers(rows, range, limit = 5) {
    const spendById = new Map();
    for (const row of rows) {
        if (row.businessDate < range.from || row.businessDate > range.to)
            continue;
        const existing = spendById.get(row.customerAccountId);
        if (existing) {
            existing.spend = (existing.spend + row.spendMd);
            existing.count += 1;
        }
        else {
            spendById.set(row.customerAccountId, {
                name: row.customerName,
                spend: row.spendMd,
                count: 1,
            });
        }
    }
    const ranked = [...spendById.entries()]
        .sort(([, a], [, b]) => {
        if (a.spend !== b.spend)
            return a.spend > b.spend ? -1 : 1;
        return a.name.localeCompare(b.name);
    })
        .slice(0, Math.max(limit, 0));
    const shown = (0, money_1.sum)(ranked.map(([, entry]) => entry.spend));
    return ranked.map(([customerAccountId, entry]) => ({
        customerAccountId,
        customerName: entry.name,
        spendMd: (0, money_1.serializeMd)(entry.spend),
        billCount: entry.count,
        sharePercent: percentOfMd(entry.spend, shown),
    }));
}
/**
 * Products at or below their low-stock threshold (§5.1, §6.3).
 *
 * A threshold of `0` disables the alert for that product — otherwise every
 * zero-stock discontinued line would sit permanently on the owner's dashboard.
 * Sorted most-urgent first: out of stock, then deepest below threshold.
 */
function lowStockAlerts(products, defaultThreshold, limit = 10) {
    const items = [];
    for (const product of products) {
        const threshold = product.lowStockThreshold ?? defaultThreshold;
        if (!Number.isFinite(threshold) || threshold <= 0)
            continue;
        const quantity = Number.isFinite(product.stockQuantity) ? product.stockQuantity : 0;
        if (quantity > threshold)
            continue;
        items.push({
            productId: product.productId,
            productName: product.productName,
            stockQuantity: quantity,
            threshold,
            isOutOfStock: quantity <= 0,
        });
    }
    return items
        .sort((a, b) => {
        if (a.isOutOfStock !== b.isOutOfStock)
            return a.isOutOfStock ? -1 : 1;
        const deficitA = safeRatio(a.stockQuantity, a.threshold, 0);
        const deficitB = safeRatio(b.stockQuantity, b.threshold, 0);
        if (deficitA !== deficitB)
            return deficitA - deficitB;
        return a.productName.localeCompare(b.productName);
    })
        .slice(0, Math.max(limit, 0));
}
/**
 * Assemble a period's money lines.
 *
 * ```
 * refillsNet = refills - refillReturns
 * netProfit  = (reservationSales + cafeSales + serviceCharges + refillsNet + incomes)
 *            - (expenses + reservationDiscounts + cafeDiscounts)
 * ```
 *
 * **A balance refund comes off the Refills line, once.** It used to leave
 * Refills gross and come off net profit separately; the owner asked for the
 * refund to be subtracted from Refills itself (2026-10-14), and netting it there
 * AND deducting it again would take it off twice. Net profit is therefore the
 * same figure the old arrangement gave on every input - the refund still comes
 * off exactly once - and only the place it is subtracted moved. `refillReturnsMd`
 * stays on the summary as the second half of the arithmetic, not as a further
 * deduction.
 *
 * Service charge counts as money taken in but stays out of Sales: Sales sums
 * bill subtotals, and `Bill.serviceChargeMd` is kept beside the subtotal, never
 * inside it. `buildOverallReport` must carry the same line for the two screens
 * to keep agreeing.
 *
 * This is `buildOverallReport`'s formula, deliberately duplicated rather than
 * approximated — see the note on {@link PeriodSummary}.
 *
 * **Refills count toward net profit but not toward Sales.** Strictly a deposit
 * is a liability until it is spent, so this overstates profit on a day of heavy
 * top-ups and understates it on the day that credit is played off. The venue's
 * incumbent system counts it this way and §14's real figures reconcile to it,
 * so matching keeps the owner's historical numbers comparable. The two lines are
 * reported separately either way, so the split is always visible.
 *
 * **The recharge bonus is not an input here.** Free credit the venue granted
 * itself is not takings, and the report excludes it from both Incomes and Net
 * Profit; passing it in anywhere would double-count promotional credit as
 * revenue.
 */
function summarisePeriod(range, current, previousSales) {
    const sales = (0, money_1.sum)([current.reservationSales, current.cafeSales]);
    // The mirror of `sales`, from the same two kinds. NOT part of `deductions`
    // below on its own account — the two lines that make it up are already there
    // individually, and adding this as well would deduct every discount twice.
    const discounts = (0, money_1.sum)([current.reservationDiscounts, current.cafeDiscounts]);
    const serviceCharges = current.serviceCharges ?? money_1.ZERO_MD;
    // Top-ups less refunds, each already summed over its own business days. Not
    // clamped at zero: a period that paid out more than it took in reads below it.
    const refills = (0, money_1.subtract)(current.refills, current.refillReturns);
    const gross = (0, money_1.sum)([sales, serviceCharges, refills, current.incomes]);
    // The refund is NOT in here: it is already inside `refills` above.
    const deductions = (0, money_1.sum)([current.expenses, current.reservationDiscounts, current.cafeDiscounts]);
    const netProfit = (0, money_1.subtract)(gross, deductions);
    return {
        range,
        reservationSalesMd: (0, money_1.serializeMd)(current.reservationSales),
        cafeSalesMd: (0, money_1.serializeMd)(current.cafeSales),
        refillMd: (0, money_1.serializeMd)(refills),
        refillTopUpsMd: (0, money_1.serializeMd)(current.refills),
        salesMd: (0, money_1.serializeMd)(sales),
        reservationDiscountsMd: (0, money_1.serializeMd)(current.reservationDiscounts),
        cafeDiscountsMd: (0, money_1.serializeMd)(current.cafeDiscounts),
        discountsMd: (0, money_1.serializeMd)(discounts),
        discountPercentOfSales: sales > 0n ? percentOfMd(discounts, sales) : null,
        serviceChargeMd: (0, money_1.serializeMd)(serviceCharges),
        refillReturnsMd: (0, money_1.serializeMd)(current.refillReturns),
        expensesMd: (0, money_1.serializeMd)(current.expenses),
        incomesMd: (0, money_1.serializeMd)(current.incomes),
        netProfitMd: (0, money_1.serializeMd)(netProfit),
        salesComparison: compareToPrevious(sales, previousSales),
    };
}
/** The estimate, from what has been billed plus what is still running. */
function estimateSales(settled, running) {
    const unbilled = (0, money_1.sum)(running.map((session) => session.accruedMd));
    const accrual = (0, money_1.sum)(running.map((session) => session.ratePerHourMd));
    return {
        settledMd: (0, money_1.serializeMd)(settled),
        unbilledMd: (0, money_1.serializeMd)(unbilled),
        totalMd: (0, money_1.serializeMd)((0, money_1.sum)([settled, unbilled])),
        accrualPerHourMd: (0, money_1.serializeMd)(accrual),
        runningSessions: running.length,
    };
}
/**
 * The estimate carried forward `elapsedSeconds` past the instant it was taken.
 *
 * Pure, so the ticking number on the dashboard is unit-tested rather than being
 * a `setInterval` nobody ever checked. Truncated to whole millidinars in bigint
 * space — a float here would drift visibly over an evening — and floored at the
 * figure itself, so a stale payload or a clock that jumped backwards can never
 * make the day appear to lose money.
 */
function tickEstimatedSales(estimate, elapsedSeconds) {
    const base = (0, money_1.fromMillidinars)(estimate.totalMd);
    if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0)
        return base;
    const perHour = BigInt(estimate.accrualPerHourMd);
    const accrued = (perHour * BigInt(Math.floor(elapsedSeconds))) / 3600n;
    return (0, money_1.fromMillidinars)(base + accrued);
}
/**
 * The zero payload — a venue with no data at all.
 *
 * Returned verbatim on the first morning of a new venue, and used as the loading
 * skeleton's shape so the screen never flashes a different layout when real
 * figures arrive. Every number is a real zero: no nulls to guard, no NaN.
 */
function emptyDashboardPayload(venueId, businessDate, period = 'DAY', currencySymbol = 'IQD') {
    const dayRange = periodRange(businessDate, 'DAY');
    const monthRange = periodRange(businessDate, 'MONTH');
    const zero = {
        reservationSales: money_1.ZERO_MD,
        cafeSales: money_1.ZERO_MD,
        refills: money_1.ZERO_MD,
        reservationDiscounts: money_1.ZERO_MD,
        cafeDiscounts: money_1.ZERO_MD,
        refillReturns: money_1.ZERO_MD,
        expenses: money_1.ZERO_MD,
        incomes: money_1.ZERO_MD,
    };
    return {
        venueId,
        businessDate,
        period,
        generatedAt: new Date(0).toISOString(),
        day: summarisePeriod(dayRange, zero, money_1.ZERO_MD),
        month: summarisePeriod(monthRange, zero, money_1.ZERO_MD),
        estimatedSales: estimateSales(money_1.ZERO_MD, []),
        monthlyTarget: computeTargetProgress(money_1.ZERO_MD, money_1.ZERO_MD),
        monthlySales: buildMonthlySalesSeries([], businessDate.slice(0, 7)),
        deviceUsage: [],
        topDevices: [],
        occupancy: computeOccupancy([]),
        floorDevices: [],
        salesSeries: buildSalesSeries([], period === 'DAY' ? dayRange : monthRange, period),
        topCustomers: [],
        lowStock: [],
        activeShifts: [],
        // Null, not an empty summary: this payload knows nothing about who is
        // looking, and an empty box list would tell a cashier "nobody holds any
        // cash" rather than "not yours to see".
        cashInBoxes: null,
        // Off, with nothing to show: every venue starts there, and this payload
        // knows nothing about the venue's settings.
        cashDrawer: { enabled: false, expectedCashMd: null, openedAt: null, notCountedSince: null },
        currencySymbol,
    };
}
/* -------------------------------------------------------------------------- */
/* Query helper                                                               */
/* -------------------------------------------------------------------------- */
/**
 * The half-open UTC interval `[start, end)` covering a business-date range —
 * for the rare query that must filter on an instant column (live sessions,
 * `openedAt`) rather than on the denormalised `businessDate`.
 *
 * Reporting queries should filter on `businessDate` directly and never need this.
 */
function rangeToInstantBounds(range, config) {
    return {
        start: (0, business_day_1.businessDayBounds)(range.from, config).start,
        end: (0, business_day_1.businessDayBounds)(range.to, config).end,
    };
}
//# sourceMappingURL=dashboard.js.map